import { NavItem, NavListItemWithBlockDescription, Select, SelectProps } from '@billy/design-system'
import omit from 'lodash/omit'
import React, { forwardRef, ReactElement, Ref, useCallback, useMemo } from 'react'
import { useQuery } from 'react-query'

import { QueryKeys } from '../../../enums/queryKeys'
import { NumberValue } from '../../../utils'
import { fetchCurrencies } from './query-api'
import { Currency } from './types/currency'
import { CurrencyValue } from './types/currencyValue'
import { getItems } from './utils/getItems'

interface CurrencySelectProps
  extends Omit<SelectProps<CurrencyValue>, 'items' | 'getDisplayValue' | 'dropdownItemRender' | 'onSelect'> {
  onSelect?: (id?: string, value?: Currency) => void
  referenceCurrency?: string
}

export const CurrencySelect = forwardRef(
  (
    { onSelect, referenceCurrency, ...selectProps }: CurrencySelectProps,
    ref: Ref<HTMLDivElement | null>,
  ): ReactElement => {
    const { data } = useQuery([QueryKeys.Currencies], fetchCurrencies)

    const items = useMemo(() => (data?.currencies ? getItems(data?.currencies) : []), [data?.currencies])

    const getDisplayValue = useCallback((item?: NavItem<CurrencyValue>) => {
      if (item) {
        return item.value.id
      }

      return ''
    }, [])

    const handleSelect = useCallback(
      (id?: string, value?: CurrencyValue) => {
        const valueFiltered = omit(value, 'displayData')

        // Since billy currencies response has exchange rates relative to the 'DKK' currency, the exchange rate needs
        // to be adjusted with reference to the organization's base currency.
        if (referenceCurrency) {
          const referenceCurrencyData = items.find((item) => item.value.id === referenceCurrency)
          const referenceCurrencyExchangeRate = referenceCurrencyData?.value?.exchangeRate

          if (referenceCurrencyExchangeRate && valueFiltered.exchangeRate) {
            valueFiltered.exchangeRate = new NumberValue(referenceCurrencyExchangeRate)
              .divide(valueFiltered.exchangeRate)
              .getRounded()
          }
        }

        onSelect?.(id, valueFiltered)
      },
      [items, onSelect, referenceCurrency],
    )

    return (
      <Select
        {...selectProps}
        dropdownItemRender={(props) => <NavListItemWithBlockDescription {...props} />}
        getDisplayValue={getDisplayValue}
        items={items}
        onSelect={handleSelect}
        ref={ref}
      />
    )
  },
)
